import React from 'react';
import { Typography, Paper, Grid, Card, CardContent } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

const DashboardCallAnalytics = () => {
  const analyticsFeatures = [
    { 
      icon: <AccessTimeIcon />, 
      title: "Call Duration Metrics", 
      description: "Analyze average call durations and identify trends over time."
    },
    { 
      icon: <TrendingUpIcon />, 
      title: "Performance Trends", 
      description: "Visualize AI Talker performance improvements and areas needing attention."
    },
    { 
      icon: <SentimentSatisfiedAltIcon />, 
      title: "Sentiment Analysis", 
      description: "Gauge caller satisfaction and emotional responses during interactions."
    },
    { 
      icon: <RecordVoiceOverIcon />, 
      title: "Speech Analytics", 
      description: "Analyze speech patterns, tone, and language use in AI conversations."
    },
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>Dashboard Call Analytics</Typography>
      <Typography paragraph>
        The Call Analytics section of your Dashboard provides deep insights into your AI Talker's performance and effectiveness. 
        By analyzing various metrics and trends, you can optimize your AI-powered communication strategy and enhance user satisfaction.
      </Typography>
      <Typography variant="h6" gutterBottom>Key Features:</Typography>
      <Grid container spacing={3}>
        {analyticsFeatures.map((feature, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card>
              <CardContent>
                {feature.icon}
                <Typography variant="h6" component="div">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Typography variant="h6" style={{ marginTop: '20px' }}>Understanding Your Analytics:</Typography>
      <Typography paragraph>
        1. <strong>Call Volume and Duration:</strong> Track the number of calls handled by your AI Talker and their average duration. This helps in capacity planning and identifying efficiency improvements.
      </Typography>
      <Typography paragraph>
        2. <strong>Resolution Rate:</strong> Monitor the percentage of calls successfully resolved by the AI Talker without human intervention. This metric is crucial for assessing the AI's effectiveness.
      </Typography>
      <Typography paragraph>
        3. <strong>User Satisfaction:</strong> Analyze sentiment scores and user feedback to gauge overall satisfaction with AI interactions.
      </Typography>
      <Typography paragraph>
        4. <strong>Common Topics and Issues:</strong> Identify frequently discussed topics and common issues to inform your knowledge base updates and AI training.
      </Typography>
      <Typography paragraph>
        5. <strong>Peak Usage Times:</strong> Understand when your AI Talker is most active to optimize resource allocation and availability.
      </Typography>
      <Typography variant="h6" style={{ marginTop: '20px' }}>Leveraging Call Analytics:</Typography>
      <Typography paragraph>
        1. Regularly review your analytics dashboard to stay informed about your AI Talker's performance.
      </Typography>
      <Typography paragraph>
        2. Use the insights gained to refine your AI Talker's responses and improve its knowledge base.
      </Typography>
      <Typography paragraph>
        3. Identify trends and patterns to proactively address potential issues or capitalize on successful strategies.
      </Typography>
      <Typography paragraph>
        4. Share relevant analytics with your team to align on goals and demonstrate the value of your AI Talker implementation.
      </Typography>
      <Typography paragraph>
        Call Analytics are a powerful tool for continuous improvement of your AI Talker system. By regularly analyzing these metrics, 
        you can ensure that your AI-powered communication strategy remains effective and aligned with your business goals.
      </Typography>
    </Paper>
  );
};

export default DashboardCallAnalytics;
